<template>
    <div class="mx-auto w-full max-w-4xl space-y-6">
        <div class="flex items-center justify-between">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Create Question
            </h1>
            <router-link
                to="/question-banks"
                class="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
            >
                ← Back to Questions
            </router-link>
        </div>

        <!-- AI Generator (dry-run: drafts load into the form for review) -->
        <div
            v-if="authStore.hasPermission('ai-assistant.use')"
            class="rounded-lg border border-primary-200 bg-white shadow dark:border-primary-800 dark:bg-gray-800"
        >
            <button
                type="button"
                class="flex w-full items-center justify-between px-6 py-4"
                @click="showGenerator = !showGenerator"
            >
                <span
                    class="inline-flex items-center text-sm font-semibold text-gray-900 dark:text-white"
                >
                    <Sparkles class="mr-2 h-5 w-5 text-primary-500" />
                    {{ t("questionBank.aiGenerator.title") }}
                </span>
                <ChevronDown
                    class="h-5 w-5 text-gray-400 transition-transform"
                    :class="{ 'rotate-180': showGenerator }"
                />
            </button>

            <div
                v-if="showGenerator"
                class="space-y-4 border-t border-gray-200 px-6 py-5 dark:border-gray-700"
            >
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ t("questionBank.aiGenerator.description") }}
                </p>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{ t("questionBank.aiGenerator.subject") }}
                        </label>
                        <select
                            v-model="generator.subjectId"
                            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="" disabled>
                                {{
                                    t(
                                        "questionBank.aiGenerator.subjectPlaceholder",
                                    )
                                }}
                            </option>
                            <option
                                v-for="s in subjects"
                                :key="s.id"
                                :value="s.id"
                            >
                                {{ s.name }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{ t("questionBank.aiGenerator.topic") }}
                        </label>
                        <input
                            v-model="generator.topic"
                            type="text"
                            :placeholder="
                                t('questionBank.aiGenerator.topicPlaceholder')
                            "
                            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{ t("questionBank.aiGenerator.count") }}
                        </label>
                        <input
                            v-model.number="generator.count"
                            type="number"
                            min="1"
                            max="10"
                            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{ t("questionBank.aiGenerator.points") }}
                        </label>
                        <input
                            v-model.number="generator.points"
                            type="number"
                            min="0.5"
                            max="100"
                            step="0.5"
                            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{ t("questionBank.aiGenerator.difficulty") }}
                        </label>
                        <select
                            v-model="generator.difficulty"
                            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="mixed">
                                {{ t("questionBank.aiGenerator.mixed") }}
                            </option>
                            <option value="easy">
                                {{ t("questionBank.easy") }}
                            </option>
                            <option value="medium">
                                {{ t("questionBank.medium") }}
                            </option>
                            <option value="hard">
                                {{ t("questionBank.hard") }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <span
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{ t("questionBank.aiGenerator.types") }}
                        </span>
                        <div class="flex flex-wrap gap-3">
                            <label
                                v-for="typeOption in typeOptions"
                                :key="typeOption.value"
                                class="flex items-center space-x-1.5 text-sm text-gray-700 dark:text-gray-300"
                            >
                                <input
                                    :value="typeOption.value"
                                    v-model="generator.types"
                                    type="checkbox"
                                    class="rounded border-gray-300"
                                />
                                <span>{{ typeOption.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button
                        type="button"
                        class="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="
                            generating ||
                            !generator.subjectId ||
                            !generator.topic.trim()
                        "
                        @click="generate"
                    >
                        <Loader2
                            v-if="generating"
                            class="mr-2 h-4 w-4 animate-spin"
                        />
                        <Sparkles v-else class="mr-2 h-4 w-4" />
                        {{
                            generating
                                ? t("questionBank.aiGenerator.generating")
                                : t("questionBank.aiGenerator.generate")
                        }}
                    </button>
                </div>

                <div v-if="drafts.length > 0" class="space-y-3">
                    <h3
                        class="text-sm font-semibold text-gray-900 dark:text-white"
                    >
                        {{ t("questionBank.aiGenerator.draftsTitle") }}
                    </h3>
                    <div
                        v-for="(draft, index) in drafts"
                        :key="index"
                        class="rounded-md border border-gray-200 p-4 dark:border-gray-700"
                        :class="{
                            'border-primary-400 dark:border-primary-600':
                                activeDraftIndex === index,
                        }"
                    >
                        <div
                            class="mb-2 flex items-start justify-between gap-3"
                        >
                            <p class="text-sm text-gray-900 dark:text-white">
                                {{ draft.question }}
                            </p>
                            <button
                                type="button"
                                class="inline-flex shrink-0 items-center rounded-md border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:border-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20"
                                @click="useDraft(index)"
                            >
                                {{
                                    activeDraftIndex === index
                                        ? t("questionBank.aiGenerator.inForm")
                                        : t("questionBank.aiGenerator.useDraft")
                                }}
                            </button>
                        </div>
                        <div class="flex flex-wrap items-center gap-2 text-xs">
                            <span
                                class="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            >
                                {{ typeLabel(draft.type) }}
                            </span>
                            <span
                                class="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            >
                                {{ draft.points }} pts · {{ draft.difficulty }}
                            </span>
                            <span
                                v-for="(option, optionIndex) in draft.options"
                                :key="optionIndex"
                                class="rounded px-2 py-0.5"
                                :class="
                                    option.is_correct
                                        ? 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300'
                                        : 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                "
                            >
                                {{ option.option_text }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <QuestionBankForm
                :key="formKey"
                :question="activeDraft ?? undefined"
                :is-editing="false"
                @saved="handleSaved"
                @cancel="handleCancel"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { ChevronDown, Loader2, Sparkles } from "@lucide/vue";
import QuestionBankForm from "@/modules/question-banks/components/QuestionBankForm.vue";
import NotificationService from "@/utils/notifications";
import aiService from "@/api/ai";
import type { AiQuestionDraft } from "@/api/ai";
import { runAiTask } from "@/composables/useAiTask";
import { subjectApi, type Subject } from "@/api/subject";
import { useAuthStore } from "@/stores/authStore";

const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();

const showGenerator = ref(false);
const subjects = ref<Subject[]>([]);
const generating = ref(false);
const drafts = ref<AiQuestionDraft[]>([]);
const activeDraftIndex = ref<number | null>(null);
const formKey = ref(0);

const generator = ref({
    subjectId: "",
    topic: "",
    count: 5,
    points: 10,
    difficulty: "mixed",
    types: ["multiple_choice"] as string[],
});

const typeOptions = computed(() => [
    { value: "multiple_choice", label: t("questionBank.typeMultipleChoice") },
    { value: "multiple_select", label: t("questionBank.typeMultipleSelect") },
    { value: "true_false", label: t("questionBank.typeTrueFalse") },
    { value: "essay", label: t("questionBank.typeEssay") },
]);

const activeDraft = computed(() => {
    if (activeDraftIndex.value === null) return null;

    const draft = drafts.value[activeDraftIndex.value];

    if (!draft) return null;

    return { ...draft, subject_id: generator.value.subjectId };
});

const typeLabel = (type: string): string =>
    typeOptions.value.find((option) => option.value === type)?.label ?? type;

const fetchSubjects = async (): Promise<void> => {
    try {
        const response = await subjectApi.getAll({ per_page: 1000 });
        subjects.value = response.data?.items || [];
    } catch (error) {
        console.error("Failed to load subjects:", error);
    }
};

const generate = async (): Promise<void> => {
    if (generating.value || !generator.value.subjectId) return;

    generating.value = true;

    try {
        drafts.value = await runAiTask<AiQuestionDraft[]>(() =>
            aiService.generateQuestionDrafts({
                subject_id: generator.value.subjectId,
                topic: generator.value.topic.trim(),
                count: generator.value.count,
                types: generator.value.types,
                points: generator.value.points,
                difficulty: generator.value.difficulty,
            }),
        );
        activeDraftIndex.value = null;
        NotificationService.success(
            t("questionBank.aiGenerator.generated", {
                count: drafts.value.length,
            }),
        );
    } catch (error) {
        console.error("AI generation failed:", error);
        NotificationService.error(
            (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t("questionBank.aiGenerator.generateFailed"),
        );
    } finally {
        generating.value = false;
    }
};

const useDraft = (index: number): void => {
    activeDraftIndex.value = index;
    // Remount the form so the draft loads into a clean state.
    formKey.value++;
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
};

const handleSaved = () => {
    NotificationService.success("Question created successfully!");
    router.push("/question-banks");
};

const handleCancel = () => {
    router.push("/question-banks");
};

onMounted(() => {
    fetchSubjects();
});
</script>
